import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from './client';

/*
 * Reports (Phase 2.6). All endpoints are POST because filter bodies may
 * carry PHI-scoped UUIDs and date ranges — GET would leak them into server
 * access logs. Exports stream CSV server-side (no client-side CSV assembly)
 * and produce an audit row on every export.
 */

export interface BillingArFilter {
    clientId?: string;
    payerName?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
}

export interface BillingArKpis {
    totalClaims: number;
    totalBilled: number;
    totalAllowed: number;
    totalPaid: number;
    totalOutstanding: number;
    totalPrBalance: number;
    collectionRate: number;
}

export interface BillingArStatusRow {
    status: string;
    count: number;
    billed: number;
    paid: number;
    outstanding: number;
}

export interface BillingArPayerRow {
    payer: string;
    count: number;
    billed: number;
    paid: number;
    outstanding: number;
}

export interface BillingArAgingRow {
    bucket: '0-30' | '31-60' | '61-90' | '90+';
    amount: number;
    count: number;
}

export interface BillingArPrRow {
    clientId: string | null;
    clientName: string;
    prBalance: number;
    openClaims: number;
}

export interface BillingArTypeRow {
    /** AR source: electronically-submitted claim, manually-submitted claim,
     *  or private-pay invoice. */
    type: 'Claim' | 'Invoice';
    /** 'Electronic' / 'Manual' for claims; '—' for invoices. */
    method: string;
    count: number;
    outstanding: number;
}

export interface BillingArReport {
    kpis: BillingArKpis;
    statusBreakdown: BillingArStatusRow[];
    payerBreakdown: BillingArPayerRow[];
    aging: BillingArAgingRow[];
    typeBreakdown: BillingArTypeRow[];
    prOutstanding: BillingArPrRow[];
}

async function fetchBillingAr(filter: BillingArFilter): Promise<BillingArReport> {
    const res = await api.post<BillingArReport>('/reports/billing-ar', filter);
    return res.data;
}

export function useBillingArReport(filter: BillingArFilter) {
    // Memo-keying on the concrete filter values keeps the cache lookup stable
    // across renders without hashing the whole object.
    return useQuery({
        queryKey: [
            'reports', 'billing-ar',
            filter.clientId ?? '',
            filter.payerName ?? '',
            filter.status ?? '',
            filter.dateFrom ?? '',
            filter.dateTo ?? '',
        ] as const,
        queryFn: () => fetchBillingAr(filter),
        staleTime: 30_000,
    });
}

/**
 * Exports the filtered Billing & AR rows as CSV and triggers a browser
 * download. Returns the rough row count (parsed from the CSV body) so the
 * caller can show a toast or similar — or null when the parse fails.
 */
export function useExportBillingAr() {
    return useExportMutation('/reports/billing-ar/export', 'billing-ar.csv');
}

// ── Session Activity ───────────────────────────────────────────────────────

export interface SessionActivityFilter {
    clientId?: string;
    staffId?: string;
    status?: 'Scheduled' | 'Complete' | 'Finalized' | 'Canceled';
    sessionTypeId?: string;
    cancelReasonId?: string;
    noteStatus?: 'Missing' | 'Drafting' | 'Submitted';
    dateFrom?: string;
    dateTo?: string;
}

export interface SessionActivityKpis {
    total: number;
    scheduled: number;
    completed: number; // Complete + Finalized
    canceled: number;
    notesMissing: number;
    notesDraft: number;
    notesSubmitted: number;
}

export interface SessionActivityStaffRow {
    staffId: string | null;
    staffName: string;
    total: number;
    completed: number;
    canceled: number;
    notesMissing: number;
}

export interface SessionActivityReasonRow {
    cancelReasonId: string | null;
    label: string;
    count: number;
}

export interface SessionActivityClientRow {
    clientId: string;
    clientName: string;
    total: number;
    completed: number;
    canceled: number;
}

export interface SessionActivityReport {
    kpis: SessionActivityKpis;
    byStaff: SessionActivityStaffRow[];
    byCancelReason: SessionActivityReasonRow[];
    byClient: SessionActivityClientRow[];
}

export function useSessionActivityReport(filter: SessionActivityFilter) {
    return useQuery({
        queryKey: [
            'reports', 'session-activity',
            filter.clientId ?? '', filter.staffId ?? '', filter.status ?? '',
            filter.sessionTypeId ?? '', filter.cancelReasonId ?? '',
            filter.noteStatus ?? '', filter.dateFrom ?? '', filter.dateTo ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<SessionActivityReport>('/reports/session-activity', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportSessionActivity() {
    return useExportMutation<SessionActivityFilter>('/reports/session-activity/export', 'session-activity.csv');
}

// ── Cancellation Rollup ────────────────────────────────────────────────────

export interface CancellationRollupFilter {
    clientId?: string;
    payerName?: string;
    cancelReasonId?: string;
    dateFrom?: string;
    dateTo?: string;
}

export interface CancellationRollupKpis {
    total: number;
    months: number;
    reasons: number;
}

export interface CancellationReasonRow {
    cancelReasonId: string | null;
    label: string;
    count: number;
}

export interface CancellationPayerRow {
    payer: string;
    count: number;
}

export interface CancellationMonthRow {
    month: string;
    count: number;
}

export interface CancellationMatrixRow {
    cancelReasonId: string | null;
    label: string;
    counts: Record<string, number>;
}

export interface CancellationRollupReport {
    kpis: CancellationRollupKpis;
    byReason: CancellationReasonRow[];
    byPayer: CancellationPayerRow[];
    byMonth: CancellationMonthRow[];
    matrix: {
        months: string[];
        reasons: CancellationMatrixRow[];
    };
}

export function useCancellationRollupReport(filter: CancellationRollupFilter) {
    return useQuery({
        queryKey: [
            'reports', 'cancellation-rollup',
            filter.clientId ?? '', filter.payerName ?? '',
            filter.cancelReasonId ?? '',
            filter.dateFrom ?? '', filter.dateTo ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<CancellationRollupReport>('/reports/cancellation-rollup', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportCancellationRollup() {
    return useExportMutation<CancellationRollupFilter>('/reports/cancellation-rollup/export', 'cancellation-rollup.csv');
}

// ── Authorization Utilization ─────────────────────────────────────────────

export type AuthUtilStatus = 'Active' | 'Expiring' | 'Expired' | 'Pending' | 'HighUtil';

export interface AuthUtilizationFilter {
    clientId?: string;
    payerName?: string;
    code?: string;
    status?: AuthUtilStatus;
}

export interface AuthUtilizationKpis {
    totalAuths: number;
    active: number;
    expiring: number;
    expired: number;
    pending: number;
    highUtil: number;
    totalHours: number;
    totalUsed: number;
}

export interface AuthUtilizationRow {
    client_id: string;
    client_name: string;
    payer: string;
    auth_number: string;
    code: string;
    hours: number;
    used: number;
    remaining: number;
    pct_used: number;
    start_date: string;
    end_date: string;
    days_left: number | null;
    status: AuthUtilStatus;
    highUtil: boolean;
}

export interface AuthUtilizationPayerRow {
    payer: string;
    count: number;
    hours: number;
    used: number;
}

export interface AuthUtilizationReport {
    kpis: AuthUtilizationKpis;
    rows: AuthUtilizationRow[];
    byPayer: AuthUtilizationPayerRow[];
}

export function useAuthUtilizationReport(filter: AuthUtilizationFilter) {
    return useQuery({
        queryKey: [
            'reports', 'auth-utilization',
            filter.clientId ?? '', filter.payerName ?? '',
            filter.code ?? '', filter.status ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<AuthUtilizationReport>('/reports/auth-utilization', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportAuthUtilization() {
    return useExportMutation<AuthUtilizationFilter>('/reports/auth-utilization/export', 'auth-utilization.csv');
}

// ── Staff Performance ─────────────────────────────────────────────────────

export interface StaffPerformanceFilter {
    staffId?: string;
    roleType?: 'BCBA' | 'RBT';
    dateFrom?: string;
    dateTo?: string;
}

export interface StaffPerformanceKpis {
    totalStaff: number;
    totalSessions: number;
    completed: number; // Complete + Finalized
    canceled: number;
    notesMissing: number;
    hours: number;
    avgCompletionRate: number;
}

export interface StaffPerformanceRow {
    staff_id: string | null;
    staff_name: string;
    role_type: string;
    total: number;
    completed: number;
    canceled: number;
    scheduled: number;
    completion_rate: number;
    cancel_rate: number;
    notes_submitted: number;
    notes_draft: number;
    notes_missing: number;
    note_rate: number;
    hours: number;
}

export interface StaffPerformanceReport {
    kpis: StaffPerformanceKpis;
    staff: StaffPerformanceRow[];
}

export function useStaffPerformanceReport(filter: StaffPerformanceFilter) {
    return useQuery({
        queryKey: [
            'reports', 'staff-performance',
            filter.staffId ?? '', filter.roleType ?? '',
            filter.dateFrom ?? '', filter.dateTo ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<StaffPerformanceReport>('/reports/staff-performance', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportStaffPerformance() {
    return useExportMutation<StaffPerformanceFilter>('/reports/staff-performance/export', 'staff-performance.csv');
}

// ── Payroll ───────────────────────────────────────────────────────────────

export interface PayrollFilter {
    staffId?: string;
    roleType?: 'BCBA' | 'RBT';
    dateFrom?: string;
    dateTo?: string;
}

export interface PayrollKpis {
    totalStaff: number;
    totalSessions: number;
    totalHours: number;
}

export interface PayrollStaffRow {
    staff_id: string | null;
    staff_name: string;
    role_type: string;
    sessions: number;
    hours: number;
}

export interface PayrollReport {
    kpis: PayrollKpis;
    staff: PayrollStaffRow[];
}

export function usePayrollReport(filter: PayrollFilter) {
    return useQuery({
        queryKey: [
            'reports', 'payroll',
            filter.staffId ?? '', filter.roleType ?? '',
            filter.dateFrom ?? '', filter.dateTo ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<PayrollReport>('/reports/payroll', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportPayroll() {
    return useExportMutation<PayrollFilter>('/reports/payroll/export', 'payroll.csv');
}

// ── Availability Overlap (staff × client) ────────────────────────────────

export interface AvailabilityOverlapCommitment {
    seriesId: string;
    clientId: string;
    clientName: string;
    /** 0=Sun…6=Sat, matches native JS Date.getDay(). */
    dayOfWeek: number;
    startMin: number;
    endMin: number;
}

export interface AvailabilityOverlapCommitmentsResponse {
    commitments: AvailabilityOverlapCommitment[];
}

/**
 * Fetches the staff member's active recurring commitments (next 28 days),
 * optionally excluding a specific client so the overlap grid only surfaces
 * *competing* commitments.
 */
export function useAvailabilityOverlapCommitments(
    staffId: string | null | undefined,
    excludeClientId: string | null | undefined,
) {
    return useQuery({
        queryKey: ['reports', 'availability-overlap-commitments', staffId ?? '', excludeClientId ?? ''] as const,
        queryFn: async () => {
            const res = await api.post<AvailabilityOverlapCommitmentsResponse>(
                '/reports/availability-overlap/commitments',
                { staffId, excludeClientId: excludeClientId || undefined },
            );
            return res.data;
        },
        enabled: !!staffId,
        staleTime: 30_000,
    });
}

// ── Supervision Compliance ───────────────────────────────────────────────

export interface SupervisionFilter {
    rbtId?: string;
    bcbaId?: string;
    compliance?: 'compliant' | 'non_compliant';
    dateFrom?: string;
    dateTo?: string;
}

export interface SupervisionKpis {
    totalRbts: number;
    compliantRbts: number;
    nonCompliantRbts: number;
    totalDirectHours: number;
    totalSupervisionHours: number;
    totalIndividualContacts: number;
    overallRatioPct: number;
    ratioThresholdPct: number;
}

export interface SupervisionRbtRow {
    staff_id: string | null;
    staff_name: string;
    direct_hours: number;
    supervision_hours: number;
    individual_contacts: number;
    months_in_service: number;
    ratio_pct: number;
    compliant: boolean;
    failing_months: string[];
}

export interface SupervisionLogEntry {
    id: string;
    date: string;
    rbt_id: string | null;
    rbt_name: string;
    bcba_id: string | null;
    bcba_name: string;
    hours: number;
    method: string;
}

export interface SupervisionReport {
    kpis: SupervisionKpis;
    rbts: SupervisionRbtRow[];
    log: SupervisionLogEntry[];
}

export function useSupervisionReport(filter: SupervisionFilter) {
    return useQuery({
        queryKey: [
            'reports', 'supervision',
            filter.rbtId ?? '', filter.bcbaId ?? '',
            filter.compliance ?? '',
            filter.dateFrom ?? '', filter.dateTo ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<SupervisionReport>('/reports/supervision', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportSupervision() {
    return useExportMutation<SupervisionFilter>('/reports/supervision/export', 'supervision-compliance.csv');
}

// ── Session Notes Export (Word .docx) ────────────────────────────────────

export type SessionNotesNoteStatus = 'finalized' | 'finalized_and_complete' | 'all';

export interface SessionNotesExportFilter {
    clientId: string;
    dateFrom?: string;
    dateTo?: string;
    noteStatus?: SessionNotesNoteStatus;
}

/**
 * Downloads a Word .docx of session notes for a single client across a date
 * window. Unlike the CSV exports, the payload is a full per-session
 * disclosure (free-text notes, embedded signature PNGs). Gated on
 * `export_reports`. The server writes an audit row on every call.
 */
export function useExportSessionNotes() {
    return useMutation({
        mutationFn: async (filter: SessionNotesExportFilter): Promise<{ filename: string }> => {
            const res = await api.post('/reports/session-notes-export', filter, { responseType: 'blob' });

            const cd = res.headers['content-disposition'] as string | undefined;
            const match = cd && /filename="([^"]+)"/.exec(cd);
            const filename = match ? match[1] : 'session-notes.docx';

            const objectUrl = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);

            return { filename };
        },
    });
}

// ── Goal Mastery ─────────────────────────────────────────────────────────

export interface GoalMasteryFilter {
    clientId?: string;
    bcbaId?: string;
    /** Scopes the cohort metric only (mastered_in_window). The snapshot
     *  always reflects current state. */
    dateFrom?: string;
    dateTo?: string;
}

export interface GoalMasteryKpis {
    totalClients: number;
    totalTargets: number;
    mastered: number;
    inProgress: number;
    onHold: number;
    discontinued: number;
    masteredInWindow: number;
}

export interface GoalMasteryClientRow {
    client_id: string;
    client_name: string;
    bcba_name: string;
    total: number;
    mastered: number;
    in_progress: number;
    on_hold: number;
    discontinued: number;
    mastered_in_window: number;
    /** mastered / (total - discontinued), 0-100. */
    mastery_rate_pct: number;
}

export interface GoalMasteryTargetRow {
    targetId: string;
    name: string;
    programName: string;
    dataType: 'trial' | 'percent' | 'frequency' | 'duration' | 'task_analysis';
    status: 'In Progress' | 'Mastered' | 'On Hold' | 'Discontinued';
    masteredAt: string | null;
    masteryValue: number | null;
    masteryDirection: 'at_or_above' | 'at_or_below';
}

export interface GoalMasteryReport {
    kpis: GoalMasteryKpis;
    clients: GoalMasteryClientRow[];
    /** Present only when filter.clientId is set. */
    targets?: GoalMasteryTargetRow[];
}

export function useGoalMasteryReport(filter: GoalMasteryFilter) {
    return useQuery({
        queryKey: [
            'reports', 'goal-mastery',
            filter.clientId ?? '', filter.bcbaId ?? '',
            filter.dateFrom ?? '', filter.dateTo ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<GoalMasteryReport>('/reports/goal-mastery', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportGoalMastery() {
    return useExportMutation<GoalMasteryFilter>('/reports/goal-mastery/export', 'goal-mastery.csv');
}

// ── Client PHI Access (HIPAA §164.528 disclosure list) ─────────────────────

export interface ClientPhiAccessFilter {
    clientId: string;
    userId?: string;
    dateFrom?: string;
    dateTo?: string;
}

export interface ClientPhiAccessKpis {
    totalEvents: number;
    distinctUsers: number;
    firstEventAt: string | null;
    lastEventAt: string | null;
}

export interface ClientPhiAccessByUser {
    userId: string | null;
    userName: string;
    count: number;
}
export interface ClientPhiAccessCount {
    /** Either category, action, or YYYY-MM month. */
    label: string;
    count: number;
}
export interface ClientPhiAccessEvent {
    id: string;
    ts: string;
    userId: string | null;
    userName: string;
    category: string;
    action: string;
    subject: string;
    result: string;
}

export interface ClientPhiAccessReport {
    client: { id: string; name: string };
    coverage: { linkedUuidCount: number; nameMatchUsed: boolean };
    kpis: ClientPhiAccessKpis;
    byUser: ClientPhiAccessByUser[];
    byCategory: { category: string; count: number }[];
    byAction:   { action: string;   count: number }[];
    byMonth:    { month: string;    count: number }[];
    events: ClientPhiAccessEvent[];
    /** True when the response hit MAX_EVENTS — narrow the date window. */
    truncated: boolean;
}

export function useClientPhiAccessReport(filter: ClientPhiAccessFilter | null) {
    return useQuery({
        queryKey: [
            'reports', 'client-phi-access',
            filter?.clientId ?? '', filter?.userId ?? '',
            filter?.dateFrom ?? '', filter?.dateTo ?? '',
        ] as const,
        enabled: !!filter?.clientId,
        queryFn: async () => {
            const res = await api.post<ClientPhiAccessReport>('/reports/client-phi-access', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportClientPhiAccess() {
    return useExportMutation<ClientPhiAccessFilter>(
        '/reports/client-phi-access/export',
        'client-phi-access.csv',
    );
}

// ── Claim Denial Analysis ─────────────────────────────────────────────────

export interface ClaimDenialFilter {
    dateFrom?: string;
    dateTo?: string;
    payerId?: string;
    serviceCode?: string;
}

export interface ClaimDenialKpis {
    deniedCount: number;
    totalFiled: number;
    deniedAmount: number;
    distinctReasons: number;
    distinctPayers: number;
    /** denials / (submitted + paid + denied + voided), as a percent with one decimal. */
    denialRatePct: number;
}

export interface ClaimDenialReasonRow {
    reason: string;
    count: number;
    billedAmount: number;
}
export interface ClaimDenialPayerRow {
    payerId: string | null;
    payerName: string;
    count: number;
    billedAmount: number;
}
export interface ClaimDenialCptRow {
    cpt: string;
    count: number;
    billedAmount: number;
}
export interface ClaimDenialComboRow {
    reason: string;
    payerId: string | null;
    payerName: string;
    cpt: string;
    count: number;
    billedAmount: number;
}
export interface ClaimDenialDetail {
    id: string;
    displayId: string;
    dos: string | null;
    clientId: string;
    clientName: string;
    payerId: string | null;
    payerName: string;
    serviceCode: string;
    denialReason: string;
    billedAmount: number;
    paidAmount: number;
    adjAmount: number;
}

export interface ClaimDenialReport {
    kpis: ClaimDenialKpis;
    byReason: ClaimDenialReasonRow[];
    byPayer:  ClaimDenialPayerRow[];
    byCpt:    ClaimDenialCptRow[];
    byCombo:  ClaimDenialComboRow[];
    denials:  ClaimDenialDetail[];
}

export function useClaimDenialReport(filter: ClaimDenialFilter) {
    return useQuery({
        queryKey: [
            'reports', 'claim-denials',
            filter.dateFrom ?? '', filter.dateTo ?? '',
            filter.payerId ?? '',  filter.serviceCode ?? '',
        ] as const,
        queryFn: async () => {
            const res = await api.post<ClaimDenialReport>('/reports/claim-denials', filter);
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useExportClaimDenials() {
    return useExportMutation<ClaimDenialFilter>('/reports/claim-denials/export', 'claim-denials.csv');
}

// ── Shared export helper ────────────────────────────────────────────────────
// All report exports share the same download + row-count plumbing. Factoring
// it keeps the per-report hook down to one line so new reports don't each
// re-implement blob plumbing slightly differently.

function useExportMutation<TFilter>(url: string, fallbackName: string) {
    return useMutation({
        mutationFn: async (filter: TFilter): Promise<{ filename: string; rows: number | null }> => {
            const res = await api.post(url, filter ?? {}, { responseType: 'blob' });

            const cd = res.headers['content-disposition'] as string | undefined;
            const match = cd && /filename="([^"]+)"/.exec(cd);
            const filename = match ? match[1] : fallbackName;

            const objectUrl = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);

            let rows: number | null = null;
            try {
                const text = await res.data.text();
                rows = Math.max(0, text.split('\r\n').filter(Boolean).length - 1);
            } catch {
                rows = null;
            }
            return { filename, rows };
        },
    });
}
